import { Action, CombinedState, combineReducers, Reducer } from '@reduxjs/toolkit';
import type { OverviewState } from 'app/pages/overview/store';
import type { PayablesState } from 'app/pages/payables/store';
import type { CardsState } from 'app/pages/store';
import flexCoding, { FlexCodingState } from 'app/pages/store/flexCodingSlice';
import flexField, { FlexFieldState } from 'app/pages/store/flexFieldSlice';
import jobCode, { JobCodeState } from 'app/pages/store/jobCodeSlice';
import erpConnection from 'app/pages/store/erpConnectionSlice';
import type { ERPConnectionInfo } from 'app/services/erp-connection.service';
import type { AuthState } from 'app/store/authSlice';
import auth from 'app/store/authSlice';
import type { DialogState } from 'app/store/dialogSlice';
import dialog from 'app/store/dialogSlice';
import type { FeatureFlagsState } from 'app/store/featureFlagsSlice';
import featureFlags from 'app/store/featureFlagsSlice';
import type { SplitDraftsState } from 'app/store/splitDraftsSlice';
import splitDrafts from 'app/store/splitDraftsSlice';
import type { NavigationState } from 'app/store/navigationSlice';
import navigation from 'app/store/navigationSlice';
import { OnboardState } from 'app/store/onboardSlice';
import searchAndFilter from 'app/store/filtering/searchAndFilterSlice';
import universalTable from 'app/store/tableSlice';
import type { TableState } from 'app/store/tableSlice';
import type { UserCompaniesState } from 'app/store/userCompaniesSlice';
import userCompanies from 'app/store/userCompaniesSlice';
import type { UserProfileState } from 'app/store/userProfileSlice';
import userProfile from 'app/store/userProfileSlice';
import type { SSOConfigState } from 'app/store/ssoConfigSlice';
import ssoConfig from 'app/store/ssoConfigSlice';
import { WalletState } from './walletSlice';
import { SearchAndFilterState } from 'util/filtering/models';

export interface RootState {
  // Static reducers; these are always loaded.
  // These must match the reducers loaded in createReducer() below.
  featureFlags: FeatureFlagsState;
  splitDrafts: SplitDraftsState;
  auth: AuthState;
  userProfile: UserProfileState;
  userCompanies: UserCompaniesState;
  navigation: NavigationState;
  dialog: DialogState;
  searchAndFilter: SearchAndFilterState;
  universalTable: TableState;
  flexCoding: FlexCodingState;
  flexField: FlexFieldState;
  jobCode: JobCodeState;
  ssoConfig: SSOConfigState;
  erpConnection: {
    connectionInfo: ERPConnectionInfo | null;
    loading: boolean;
    error: string | null;
    pendingConnectionId: string | null;
  };

  // Dynamic reducers; these are loaded at runtime.
  // Keys must be optional.
  // Replace `unknown` with actual types as reducers are ported to TS.
  onboard?: OnboardState;
  overview?: OverviewState;
  payables?: PayablesState;
  partners?: unknown;
  cards?: CardsState;
  wallet?: WalletState;
}

const createReducer =
  (asyncReducers?: Record<string, Reducer>) =>
  (state: CombinedState<RootState> | undefined, action: Action) => {
    const combinedReducer = combineReducers<RootState>({
      featureFlags,
      splitDrafts,
      auth,
      userProfile,
      userCompanies,
      navigation,
      dialog,
      searchAndFilter,
      universalTable,
      flexCoding,
      flexField,
      jobCode,
      ssoConfig,
      erpConnection,
      ...asyncReducers,
    });

    return combinedReducer(state, action);
  };

export default createReducer;
